import React, { ChangeEvent, FC, useContext, useEffect, useMemo, useState } from 'react';
import {
  daysGreaterForDayTabToDisable,
  EVENT_ANALYTICS_CONTEXT_KEYS,
  FACEBOOK_DESTINATION_TYPES,
  FACEBOOK_PAGE_STATE_CONTEXT_KEYS,
  FB_CSV_EXPORT_FIELDS,
  MAX_FETCH_LIMIT_FOR_CSV_EXPORT,
  pages,
  PAGE_VALUE,
  FACEBOOK_GROUP_TIMELINE,
  FACEBOOK_PAGE,
  TIME_WINDOW_SET,
  TIME_WINDOW_VALUES,
} from '../../event-analytics-constants';
import { FacebookContext } from '../../../../contexts/eventanalytics';
import FacebookStatsGraphView from './facebook-stats-graph-view';
import { Option } from '@resi-media/resi-ui';
import { dateRangeValues, customDateRangeOption, lifeTimeValues } from '../../date-range-options';
import {
  downloadAsCSV,
  fetchAndMergeResponse,
  parseLineChartData,
  isDayTabDisabled,
  isMonthTabDisabled,
  getDateRangeOptions,
} from '../../utils';
import { exportCSVData, fetchLineChartData } from '../api';
import { DateType, lineChartDataType, TIME_WINDOWS } from '../../types';
import { EventAnalyticsContext } from '../../../../contexts/eventanalytics/event-analytics';
import { DateTime } from 'luxon';
import { Parser } from 'json2csv';
import { MPEventName, MPEventProperty, trackMixpanelEvent } from '../../../../mixpanel';

const FacebookStatsGraph: FC = (): JSX.Element => {
  const { facebookAnalyticsState, dispatch } = useContext(FacebookContext);
  const { EventAnalyticsState, dispatch: dispatchEventAnalyticsState } = useContext(EventAnalyticsContext);
  const {
    viewAllData,
    dateRange: { startDate, endDate },
    dateRangeOption,
    eventId,
  } = EventAnalyticsState;
  const isMonthTimeWindowDisabled = isMonthTabDisabled(dateRangeOption?.value);
  const isDayTimeWindowDisabled = isDayTabDisabled(startDate, endDate);
  const [dataPage, setDataPage] = useState<number>(0);
  const [dataPageName, setDataPageName] = useState<string>(PAGE_VALUE.VIEWERS);
  const [timeWindow, setTimeWindow] = useState<number>(isDayTimeWindowDisabled ? 1 : 0);
  const [isDatePickerOpen, setDatePickerOpen] = useState<boolean>(false);
  const [timeWindowValue, setTimeWindowValue] = useState<string>(
    isDayTimeWindowDisabled ? TIME_WINDOW_VALUES.WEEK : TIME_WINDOW_VALUES.DAY
  );
  const [chartData, setChartData] = useState<lineChartDataType>({ labels: [], datasets: {} });
  const [isChartLoading, setChartLoading] = useState<boolean>(false);
  const [isExportCSVLoading, setExportCSVLoading] = useState<boolean>(false);

  const handleDataPageChange = (event: ChangeEvent<unknown>, newValue: 0 | 1 | 2) => {
    setDataPage(newValue);
    if (!eventId) {
      setDataPageName(pages[newValue]);
    } else if (facebookAnalyticsState.destinationType === FACEBOOK_DESTINATION_TYPES.PAGE) {
      setDataPageName(FACEBOOK_PAGE[newValue]);
    } else {
      setDataPageName(FACEBOOK_GROUP_TIMELINE[newValue]);
    }
  };

  const handleTimeWindowChange = (event: ChangeEvent<unknown> | null, newValue: TIME_WINDOWS) => {
    setTimeWindowValue(TIME_WINDOW_SET[newValue]);
    setTimeWindow(newValue);
  };

  const handleDateRangeChange = (item: Option[]) => {
    const selection = item[0]?.value;
    let dateRange: DateType = { startDate: '', endDate: '' };
    let numberOfDays = 0;
    // Since we have only one custom handling case "c0".
    if (selection === customDateRangeOption.value) {
      setDatePickerOpen(true);
      dispatchEventAnalyticsState({
        type: EVENT_ANALYTICS_CONTEXT_KEYS.DATE_RANGE_VALUE,
        payload: customDateRangeOption,
      });
    } else if (selection === '0' && viewAllData) {
      dateRange = lifeTimeValues[selection];
      dispatchEventAnalyticsState({ type: EVENT_ANALYTICS_CONTEXT_KEYS.DATE_RANGE_VALUE, payload: item[0] });
      dispatchEventAnalyticsState({ type: EVENT_ANALYTICS_CONTEXT_KEYS.DATE_RANGE, payload: dateRange });
      numberOfDays = Math.ceil(
        DateTime.fromISO(dateRange.endDate).diff(DateTime.fromISO(dateRange.startDate), 'days').toObject()
          .days as number
      );
    } else if (selection) {
      dateRange = dateRangeValues[selection];
      dispatchEventAnalyticsState({ type: EVENT_ANALYTICS_CONTEXT_KEYS.DATE_RANGE_VALUE, payload: item[0] });
      dispatchEventAnalyticsState({ type: EVENT_ANALYTICS_CONTEXT_KEYS.DATE_RANGE, payload: dateRange });
      numberOfDays = Math.ceil(
        DateTime.fromISO(dateRange.endDate).diff(DateTime.fromISO(dateRange.startDate), 'days').toObject()
          .days as number
      );
    }
    if (numberOfDays > daysGreaterForDayTabToDisable) {
      handleTimeWindowChange(null, 1);
    } else {
      handleTimeWindowChange(null, 0);
    }
  };

  const handleCustomDateRangeSelection = (startDate: string, endDate: string) => {
    if (startDate && endDate) {
      const dateRange = {
        startDate,
        endDate,
      };
      dispatchEventAnalyticsState({ type: EVENT_ANALYTICS_CONTEXT_KEYS.DATE_RANGE, payload: dateRange });
      const numberOfDays = Math.ceil(
        DateTime.fromISO(dateRange.endDate).diff(DateTime.fromISO(dateRange.startDate), 'days').toObject()
          .days as number
      );
      dispatchEventAnalyticsState({
        type: EVENT_ANALYTICS_CONTEXT_KEYS.DATE_RANGE_VALUE,
        payload: customDateRangeOption,
      });
      if (numberOfDays > daysGreaterForDayTabToDisable) {
        handleTimeWindowChange(null, 1);
      } else {
        handleTimeWindowChange(null, 0);
      }
    }
  };

  const getChartData = async () => {
    setChartLoading(true);
    const response = await fetchLineChartData(
      EventAnalyticsState,
      facebookAnalyticsState,
      timeWindowValue,
      dataPageName
    );
    setChartLoading(false);
    const lineChartData = parseLineChartData(response, timeWindowValue, dataPageName);
    setChartData(lineChartData);
  };

  const exportCSV = async () => {
    setExportCSVLoading(true);
    const response = await fetchAndMergeResponse(
      (offSetValue1: string | number | undefined, offSetValue2: string | number | undefined) =>
        exportCSVData(EventAnalyticsState, facebookAnalyticsState, offSetValue1, offSetValue2),
      MAX_FETCH_LIMIT_FOR_CSV_EXPORT,
      'videoId',
      'timestamp'
    );
    setExportCSVLoading(false);
    const json2csvParser = new Parser({ fields: FB_CSV_EXPORT_FIELDS });
    const csv = json2csvParser.parse(response);
    downloadAsCSV(csv, 'Facebook-Analytics.csv');

    const mixpanelProps = {
      [MPEventProperty.TRANSCODED_EVENT_UUID]: EventAnalyticsState.eventId,
      [MPEventProperty.ANALYTICS_TYPE]: 'Facebook',
    };

    if (!EventAnalyticsState.eventId) {
      delete mixpanelProps[MPEventProperty.TRANSCODED_EVENT_UUID];
    }

    trackMixpanelEvent(MPEventName.CSV_EXPORT, mixpanelProps);
  };

  useEffect(() => {
    getChartData();
  }, [facebookAnalyticsState, timeWindowValue, EventAnalyticsState, dataPageName]);

  useEffect(() => {
    if (!EventAnalyticsState.eventId) {
      dispatch({ payload: FACEBOOK_DESTINATION_TYPES.PAGE, type: FACEBOOK_PAGE_STATE_CONTEXT_KEYS.DESTINATION_TYPE });
    }
  }, [EventAnalyticsState.eventId]);

  useEffect(() => {
    setDataPage(0);
    if (!eventId) {
      setDataPageName(pages[0]);
    } else if (facebookAnalyticsState.destinationType === FACEBOOK_DESTINATION_TYPES.PAGE) {
      setDataPageName(FACEBOOK_PAGE[0]);
    } else {
      setDataPageName(FACEBOOK_GROUP_TIMELINE[0]);
    }
  }, [eventId]);

  return (
    <FacebookStatsGraphView
      data={chartData}
      dateRangeOptions={getDateRangeOptions(viewAllData)}
      isChartLoading={isChartLoading}
      isExportCSVLoading={isExportCSVLoading}
      disableDayTab={isDayTimeWindowDisabled}
      isDatePickerOpen={isDatePickerOpen}
      timeWindow={timeWindow}
      dataPageName={dataPageName}
      dataPage={dataPage}
      disableMonthTab={isMonthTimeWindowDisabled}
      handleDataPageChange={handleDataPageChange}
      handleDateRangeChange={handleDateRangeChange}
      handleTimeWindowChange={handleTimeWindowChange}
      handleCustomDateRangeSelection={handleCustomDateRangeSelection}
      exportCSV={exportCSV}
      closeDatePicker={() => setDatePickerOpen(false)}
    />
  );
};

FacebookStatsGraph.displayName = 'FacebookStatsGraph';

export default FacebookStatsGraph;
